// Tipo de alvará/licença: contrato de tipos do catálogo (secao 4.3).
//
// O tipo vivia em `mocks/alvarasConfig`, junto de um array de templates de
// mentira — importar o tipo obrigava a carregar o mock, e o mock parecia fonte
// de dado. Mesmo movimento que `lib/profissoes.ts` já tinha feito, agora que a
// migration 0048 acrescentou campos e o mock ficou ainda mais defasado.
//
// Os **dados** vêm de `alvaras_templates` pela função `config`.

import type { PeriodoParcial } from '@/lib/periodo';

/**
 * Onde o alvará é exigido — território.
 *
 * Não confundir com `esfera`, que diz quem emite. O Laudo de Para-raios é
 * `nacional` (exigido no país inteiro) e emitido por empresa especializada, não
 * pela União: são eixos diferentes, e a migration 0048 manteve os dois.
 */
export type AbrangenciaAlvara = 'nacional' | 'estadual' | 'municipal';

/** Quem emite o documento. */
export type EsferaAlvara = 'municipal' | 'estadual' | 'federal';

export const ESFERAS: { valor: EsferaAlvara; rotulo: string }[] = [
  { valor: 'municipal', rotulo: 'Municipal' },
  { valor: 'estadual', rotulo: 'Estadual' },
  { valor: 'federal', rotulo: 'Federal' },
];

export interface AlvaraTemplate {
  id: string;
  nome: string;
  descricao: string;
  categoria: string;
  /** Território onde é exigido. */
  abrangencia: AbrangenciaAlvara;
  /** Quem emite (secao 4.3). */
  esfera: EsferaAlvara;
  estados: string[];
  todasCidades: boolean;
  cidadesEspecificas: string[];
  /** Rótulo legado da vigência ('anual', 'bienal'). A fonte é `vigencia`. */
  periodicidade: string;
  obrigatorio: boolean;
  orgaoEmissor: string;
  /** Validade depois de emitido. Vazio = documento que não vence. */
  vigencia: PeriodoParcial;
  /** Prazo para obter o alvará. Vazio = sem janela definida. */
  prazo: PeriodoParcial;
  requerDocumentoAnexo: boolean;
  /** Nulo: usa a janela global de `notificacao_parametros`. */
  diasAntecedenciaAviso: number | null;
  tempoMedioGeracao: PeriodoParcial;
  /** Cobre o condomínio inteiro. Pode ser verdadeiro junto com `itens`. */
  aplicaAoCondominioGeral: boolean;
  /** Itens de infraestrutura que exigem este alvará. */
  itens: string[];
  dataCriacao: string;
  ativo: boolean;
}

/**
 * Rótulo legado a partir da vigência.
 *
 * `periodicidade` continua na tabela porque telas e relatórios a leem. Derivá-la
 * do Período aqui evita que a pessoa escolha "anual" no select e "2 anos" no
 * Período, e o registro fique afirmando as duas coisas.
 */
export function periodicidadeDaVigencia(vigencia: PeriodoParcial): string {
  if (!vigencia.valor || !vigencia.unidade) return 'unico';

  const meses =
    vigencia.unidade === 'anos'
      ? vigencia.valor * 12
      : vigencia.unidade === 'meses'
        ? vigencia.valor
        : Math.round(vigencia.valor / 30);

  const rotulos: Record<number, string> = {
    1: 'mensal',
    2: 'bimestral',
    3: 'trimestral',
    6: 'semestral',
    12: 'anual',
    24: 'bienal',
    36: 'trienal',
    60: 'quinquenal',
  };
  return rotulos[meses] ?? 'anual';
}
